import { useEffect, useState } from "react";
import { useLocation, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Server } from "lucide-react";
import { API, LogoMark } from "@/lib/site";

export default function PaymentResult() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const isSuccess = pathname.includes("success");
  const sessionId = params.get("session_id");
  const [state, setState] = useState(isSuccess ? "polling" : "cancelled");

  useEffect(() => {
    if (!isSuccess || !sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid" && data.server_id) {
          setState("provisioned");
          return;
        }
        if (data.payment_status === "paid") {
          setState("paid");
        }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts < 30) setTimeout(poll, 2000);
      else setState((s) => (s === "polling" ? "timeout" : s));
    };
    poll();
  }, [isSuccess, sessionId]);

  return (
    <div data-testid="payment-result-page" className="min-h-screen technical-grid bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-10 text-center shadow-2xl shadow-slate-900/10">
        <div className="flex justify-center mb-6"><LogoMark className="w-12 h-12" /></div>
        {state === "cancelled" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-amber-500" />
            <h1 className="mt-5 font-display font-bold text-2xl tracking-tight">Payment cancelled</h1>
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">No charge was made. You can pick a plan again whenever you're ready.</p>
            <Link to="/portal" data-testid="payment-cancel-back"
                  className="mt-8 inline-flex px-7 py-3.5 rounded-full bg-primary text-white font-semibold text-sm hover:bg-blue-700 transition-colors">
              Back to Portal
            </Link>
          </>
        )}
        {(state === "polling" || state === "paid") && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin" />
            <h1 className="mt-5 font-display font-bold text-2xl tracking-tight" data-testid="payment-provisioning-title">
              {state === "paid" ? "Payment confirmed — creating your server" : "Confirming your payment…"}
            </h1>
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Your VPS is being provisioned. This usually takes a few seconds.</p>
          </>
        )}
        {state === "provisioned" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
            <h1 className="mt-5 font-display font-bold text-2xl tracking-tight" data-testid="payment-success-title">Your server is live</h1>
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
              Payment confirmed and your VPS has been created. Its IP address, credentials and controls are waiting in your portal.
            </p>
            <Link to="/portal" data-testid="payment-success-portal"
                  className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-white font-semibold text-sm hover:bg-blue-700 transition-colors">
              <Server className="w-4 h-4" /> Open My Servers
            </Link>
          </>
        )}
        {state === "timeout" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
            <h1 className="mt-5 font-display font-bold text-2xl tracking-tight">Payment received</h1>
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Your server is being provisioned in the background — it will appear in your portal shortly.</p>
            <Link to="/portal" className="mt-8 inline-flex px-7 py-3.5 rounded-full bg-primary text-white font-semibold text-sm hover:bg-blue-700 transition-colors">
              Go to Portal
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
